export function normalizeCompanyNumber(value) {
  const compact = String(value ?? '').trim().toUpperCase().replace(/\s+/g, '');
  if (/^\d{1,8}$/.test(compact)) return compact.padStart(8, '0');
  return /^[A-Z0-9]{8}$/.test(compact) ? compact : null;
}

const FIT_LABELS = {
  potential: 'Potential match · evidence needed',
  unknown: 'Needs evidence',
  eligible: 'Eligibility confirmed',
  eligible_for_comparison: 'Fit compared · review evidence',
  not_eligible: 'Mandatory condition not met',
  unverified: 'Awaiting company check',
  excluded: 'Do not shortlist',
  review: 'Needs review',
  fail: 'Mandatory condition not met',
};

export function fitLabel(status) {
  return FIT_LABELS[status] ?? 'Needs review';
}

export function isOpenTender(status) {
  return status === 'Active tender' || status === 'Open';
}

export function countryOptions(tenders) {
  const countries = new Map();
  for (const tender of tenders ?? []) {
    const code = String(tender.countryCode ?? '').trim().toUpperCase();
    const name = String(tender.countryName ?? '').trim();
    if (code && name) countries.set(code, { code, name });
  }
  return [...countries.values()].sort((a, b) => a.name.localeCompare(b.name));
}

export function matchesCountry(tender, query) {
  const normalized = String(query ?? '').trim().toLocaleLowerCase();
  if (!normalized) return true;
  return [tender?.countryName, tender?.countryCode]
    .some((value) => String(value ?? '').toLocaleLowerCase().includes(normalized));
}

export function tenderRequirementItems(tender) {
  if (!tender) return [];
  const criteria = tender.scorecard?.criteria ?? [];
  if (criteria.length) return criteria;
  return (tender.constraints ?? []).map((condition) => ({
    ...condition,
    criterionType: ['mandatory', 'prohibited', 'waived'].includes(condition.modality)
      ? 'mandatory_condition'
      : 'supplier_or_delivery_criterion',
    status: condition.needsReview ? 'review' : 'extracted',
    citations: (condition.evidence ?? []).map((evidence) => ({ ...evidence, status: evidence.status ?? 'valid' })),
  }));
}

export function tenderCitationUrl(citation, noticeUrl) {
  const sourceUrl = citation?.url;
  if (!sourceUrl || !noticeUrl) return sourceUrl;
  try {
    const source = new URL(sourceUrl);
    const notice = new URL(noticeUrl);
    const isOcdsRecordApi = source.hostname.endsWith('find-tender.service.gov.uk')
      && source.pathname.startsWith('/api/1.0/ocdsRecordPackages/');
    const isReadableFindTenderNotice = notice.hostname.endsWith('find-tender.service.gov.uk')
      && notice.pathname.startsWith('/procurement/');
    return isOcdsRecordApi && isReadableFindTenderNotice ? noticeUrl : sourceUrl;
  } catch {
    return sourceUrl;
  }
}

export function humanizeIndustry(value) {
  const label = String(value ?? '')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
  return label.charAt(0).toUpperCase() + label.slice(1).replace(/\bit\b/g, 'IT');
}

export function formatCurrency(value) {
  if (value === null || value === undefined || value === '') return 'Value not published';
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return 'Value not published';
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(numeric);
}

export function closingLabel(date) {
  if (!date) return 'Deadline not published';
  const parsed = new Date(date);
  if (Number.isNaN(parsed.valueOf())) return date;
  return new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }).format(parsed);
}
